import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Shield, Check, Lock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { permissionGroups, permissions } from '../data/mockData';
import { PermissionGroup, Permission } from '../types';
export function RoleManagement() {
  const [groups, setGroups] = useState<PermissionGroup[]>(permissionGroups);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PermissionGroup | null>(null);
  // Form State
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    name_en: ''
  });
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  // Group permissions by module
  const permissionsByModule = permissions.reduce((acc, perm) => {
    if (!acc[perm.module]) {
      acc[perm.module] = [];
    }
    acc[perm.module].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);
  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupFormData({
      name: '',
      name_en: ''
    });
    setIsGroupModalOpen(true);
  };
  const handleEditGroup = (group: PermissionGroup) => {
    setEditingGroup(group);
    setGroupFormData({
      name: group.name,
      name_en: group.name_en
    });
    setIsGroupModalOpen(true);
  };
  const handleManagePermissions = (group: PermissionGroup) => {
    setEditingGroup(group);
    // If 'all' is in permissions, select all available permission keys
    if (group.permissions.includes('all')) {
      setSelectedPermissions(permissions.map(p => p.permission_key));
    } else {
      setSelectedPermissions(group.permissions);
    }
    setIsPermissionModalOpen(true);
  };
  const handleDeleteGroup = (id: string) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      setGroups(groups.filter(g => g.id !== id));
    }
  };
  const handleGroupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingGroup) {
      // Update existing
      setGroups(groups.map(g => g.id === editingGroup.id ? {
        ...g,
        ...groupFormData
      } : g));
    } else {
      // Create new
      const newGroup: PermissionGroup = {
        id: `pg${Date.now()}`,
        ...groupFormData,
        permissions: []
      };
      setGroups([...groups, newGroup]);
    }
    setIsGroupModalOpen(false);
  };
  const handlePermissionSubmit = () => {
    if (!editingGroup) return;
    // Check if all permissions are selected to optimize storage (optional logic)
    const allKeys = permissions.map(p => p.permission_key);
    const isAllSelected = allKeys.every(k => selectedPermissions.includes(k));
    const updatedPermissions = isAllSelected ? ['all'] : selectedPermissions;
    setGroups(groups.map(g => g.id === editingGroup.id ? {
      ...g,
      permissions: updatedPermissions
    } : g));
    setIsPermissionModalOpen(false);
  };
  const togglePermission = (key: string) => {
    if (selectedPermissions.includes(key)) {
      setSelectedPermissions(selectedPermissions.filter(k => k !== key));
    } else {
      setSelectedPermissions([...selectedPermissions, key]);
    }
  };
  const toggleModulePermissions = (moduleName: string, modulePermissions: Permission[]) => {
    const moduleKeys = modulePermissions.map(p => p.permission_key);
    const allModuleSelected = moduleKeys.every(k => selectedPermissions.includes(k));
    if (allModuleSelected) {
      // Deselect all
      setSelectedPermissions(selectedPermissions.filter(k => !moduleKeys.includes(k)));
    } else {
      // Select all (add missing ones)
      const newKeys = [...selectedPermissions];
      moduleKeys.forEach(k => {
        if (!newKeys.includes(k)) newKeys.push(k);
      });
      setSelectedPermissions(newKeys);
    }
  };
  return <div className="space-y-6" data-id="element-429">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-430">
        <div data-id="element-431">
          <h1 className="text-2xl font-bold text-slate-800" data-id="element-432">
            Role & Permission Management
          </h1>
          <p className="text-slate-500" data-id="element-433">
            Define roles and assign access permissions
          </p>
        </div>
        <Button onClick={handleCreateGroup} data-id="element-434">
          <Plus className="mr-2 h-4 w-4" data-id="element-435" /> Add New Role
        </Button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-436">
        {groups.map(group => <div key={group.id} className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 flex flex-col h-full hover:shadow-md transition-shadow" data-id="element-437">
            <div className="flex justify-between items-start mb-4" data-id="element-438">
              <div className="p-3 bg-emerald-100 rounded-lg text-emerald-600" data-id="element-439">
                <Shield size={24} data-id="element-440" />
              </div>
              <div className="flex space-x-2" data-id="element-441">
                <button onClick={() => handleEditGroup(group)} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors" data-id="element-442">
                  <Edit2 size={16} data-id="element-443" />
                </button>
                <button onClick={() => handleDeleteGroup(group.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-id="element-444">
                  <Trash2 size={16} data-id="element-445" />
                </button>
              </div>
            </div>

            <h3 className="text-lg font-bold text-slate-900" data-id="element-446">
              {group.name_en}
            </h3>
            <p className="text-sm text-slate-500 mb-6" data-id="element-447">{group.name}</p>

            <div className="mt-auto" data-id="element-448">
              <div className="flex items-center text-sm text-slate-600 mb-4" data-id="element-449">
                <Lock size={14} className="mr-2" data-id="element-450" />
                <span data-id="element-451">
                  {group.permissions.includes('all') ? 'Full System Access' : `${group.permissions.length} Permissions Assigned`}
                </span>
              </div>

              <Button variant="secondary" className="w-full" onClick={() => handleManagePermissions(group)} data-id="element-452">
                Manage Permissions
              </Button>
            </div>
          </div>)}
      </div>

      {/* Create/Edit Group Modal */}
      <Modal isOpen={isGroupModalOpen} onClose={() => setIsGroupModalOpen(false)} title={editingGroup ? 'Edit Role' : 'Create New Role'} data-id="element-453">
        <form onSubmit={handleGroupSubmit} className="space-y-4" data-id="element-454">
          <Input label="Role Name (English)" value={groupFormData.name_en} onChange={e => setGroupFormData({
          ...groupFormData,
          name_en: e.target.value
        })} placeholder="e.g. System Manager" required data-id="element-455" />
          <Input label="Role Name (Bengali)" value={groupFormData.name} onChange={e => setGroupFormData({
          ...groupFormData,
          name: e.target.value
        })} placeholder="e.g. সিস্টেম ম্যানেজার" required data-id="element-456" />
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 mt-4" data-id="element-457">
            <Button type="button" variant="secondary" onClick={() => setIsGroupModalOpen(false)} data-id="element-458">
              Cancel
            </Button>
            <Button type="submit" data-id="element-459">Save Role</Button>
          </div>
        </form>
      </Modal>

      {/* Manage Permissions Modal */}
      <Modal isOpen={isPermissionModalOpen} onClose={() => setIsPermissionModalOpen(false)} title={`Permissions for ${editingGroup?.name_en}`} size="xl" data-id="element-460">
        <div className="space-y-6" data-id="element-461">
          <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm text-blue-800" data-id="element-462">
            Select the permissions you want to assign to this role. Users with
            this role will be able to perform the selected actions.
          </div>

          <div className="space-y-6" data-id="element-463">
            {Object.entries(permissionsByModule).map(([module, modulePermissions]) => {
            const allSelected = modulePermissions.every(p => selectedPermissions.includes(p.permission_key));
            return <div key={module} className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-464">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex justify-between items-center" data-id="element-465">
                      <h4 className="font-semibold text-slate-800" data-id="element-466">
                        {module} Management
                      </h4>
                      <button type="button" onClick={() => toggleModulePermissions(module, modulePermissions)} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:underline" data-id="element-467">
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-468">
                      {modulePermissions.map(perm => <label key={perm.id} className={`flex items-start p-3 rounded-md border cursor-pointer transition-all ${selectedPermissions.includes(perm.permission_key) ? 'bg-emerald-50 border-emerald-200 ring-1 ring-emerald-200' : 'bg-white border-slate-200 hover:border-slate-300'}`} data-id="element-469">
                          <div className="flex h-5 items-center" data-id="element-470">
                            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" checked={selectedPermissions.includes(perm.permission_key)} onChange={() => togglePermission(perm.permission_key)} data-id="element-471" />
                          </div>
                          <div className="ml-3 text-sm" data-id="element-472">
                            <span className="font-medium text-slate-900 block" data-id="element-473">
                              {perm.name_en}
                            </span>
                            <span className="text-slate-500" data-id="element-474">{perm.name}</span>
                          </div>
                        </label>)}
                    </div>
                  </div>;
          })}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 sticky bottom-0 bg-white pb-2" data-id="element-475">
            <Button variant="secondary" onClick={() => setIsPermissionModalOpen(false)} data-id="element-476">
              Cancel
            </Button>
            <Button onClick={handlePermissionSubmit} data-id="element-477">Save Permissions</Button>
          </div>
        </div>
      </Modal>
    </div>;
}